import { getSmoothHour, isTimeSynced } from './time';

// ============================================================================
// Environment State - Ambient light from day/night cycle
// ============================================================================
// Composition point for future weather effects (fog, rain, lightning, snow).
// Each effect will modify _ambientLight after the base time-of-day calculation.

type AmbientLightState = { color: number; alpha: number };

// PERFORMANCE: Preallocated object, mutated in place each frame
const _ambientLight: AmbientLightState = { color: 0x000000, alpha: 0 };

export const getAmbientLight = (): Readonly<AmbientLightState> => _ambientLight;

// ============================================================================
// Keyframes - hour, r, g, b, alpha
// ============================================================================

type LightKeyframe = {
  readonly hour: number;
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly alpha: number;
};

const LIGHT_KEYFRAMES: readonly LightKeyframe[] = [
  { hour: 0, r: 0x0a, g: 0x0a, b: 0x2e, alpha: 0.55 },
  { hour: 4, r: 0x0a, g: 0x0a, b: 0x2e, alpha: 0.55 },
  { hour: 5, r: 0x1e, g: 0x14, b: 0x32, alpha: 0.45 },
  { hour: 5.5, r: 0x50, g: 0x1e, b: 0x1e, alpha: 0.3 },
  { hour: 6.5, r: 0x78, g: 0x5a, b: 0x28, alpha: 0.15 },
  { hour: 7.5, r: 0x00, g: 0x00, b: 0x00, alpha: 0 },
  { hour: 17, r: 0x00, g: 0x00, b: 0x00, alpha: 0 },
  { hour: 18, r: 0x64, g: 0x3c, b: 0x14, alpha: 0.1 },
  { hour: 19, r: 0x78, g: 0x32, b: 0x14, alpha: 0.25 },
  { hour: 20, r: 0x28, g: 0x14, b: 0x3c, alpha: 0.4 },
  { hour: 21, r: 0x0f, g: 0x0c, b: 0x32, alpha: 0.5 },
  { hour: 22, r: 0x0a, g: 0x0a, b: 0x2e, alpha: 0.55 },
  { hour: 24, r: 0x0a, g: 0x0a, b: 0x2e, alpha: 0.55 },
];

// ============================================================================
// Keyframe Interpolation
// ============================================================================

// PERFORMANCE: Default keyframe avoids conditional checks in hot path
const DEFAULT_KEYFRAME: LightKeyframe = { hour: 0, r: 0x0a, g: 0x0a, b: 0x2e, alpha: 0.55 };

const lerpKeyframes = ({ hour }: { hour: number }): void => {
  // Find bracketing keyframes
  let lo: LightKeyframe = DEFAULT_KEYFRAME;
  let hi: LightKeyframe = DEFAULT_KEYFRAME;

  for (let i = 0; i < LIGHT_KEYFRAMES.length - 1; i++) {
    const current = LIGHT_KEYFRAMES[i];
    const next = LIGHT_KEYFRAMES[i + 1];
    if (current && next && hour >= current.hour && hour <= next.hour) {
      lo = current;
      hi = next;
      break;
    }
  }

  const range = hi.hour - lo.hour;
  const t = range > 0 ? (hour - lo.hour) / range : 0;

  const r = Math.round(lo.r + (hi.r - lo.r) * t);
  const g = Math.round(lo.g + (hi.g - lo.g) * t);
  const b = Math.round(lo.b + (hi.b - lo.b) * t);

  _ambientLight.color = (r << 16) | (g << 8) | b;
  _ambientLight.alpha = lo.alpha + (hi.alpha - lo.alpha) * t;
};

// ============================================================================
// Fade-in after first time sync (avoids flash from default noon to actual time)
// ============================================================================

const FADE_IN_DURATION_MS = 800;
let _fadeInStart = 0;

// ============================================================================
// Update (called every frame from render layer)
// ============================================================================

export const updateEnvironment = (): void => {
  // Before server time sync: full black overlay hides the world
  if (!isTimeSynced()) {
    _ambientLight.color = 0x000000;
    _ambientLight.alpha = 1;
    return;
  }

  // Track when fade-in started (first frame after sync)
  if (_fadeInStart === 0) {
    _fadeInStart = performance.now();
  }

  const hour = getSmoothHour();
  lerpKeyframes({ hour });
  // Future: applyFogEffect(), applyRainDarkening(), etc.

  // Smooth fade from black to correct lighting
  const fadeElapsed = performance.now() - _fadeInStart;
  if (fadeElapsed < FADE_IN_DURATION_MS) {
    // PERFORMANCE: smoothstep easing without allocation
    const t = fadeElapsed / FADE_IN_DURATION_MS;
    const eased = t * t * (3 - 2 * t);
    _ambientLight.alpha = 1 - (1 - _ambientLight.alpha) * eased;
  }
};
